<script lang="ts" setup>
import { computed, ref } from "vue";
import EmailPresets from "./EmailPresets.vue";
import {
  useDocumentSpaceConfigsQuery,
  useUpdateUserConfigMutation,
} from "@/graphql/operations";
import { useStore } from "vuex";
import { Dropdown } from "@/components";
import { useFlashMessage } from "@/hooks";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const store = useStore();
const { displayFlashMessage } = useFlashMessage();

const textElem = ref();

const { data: configData, fetching: fetchingConfig } =
  useDocumentSpaceConfigsQuery();

const numSpaceConfigs = computed(
  () => configData.value?.documentSpaceConfigs?.totalCount || 0,
);

const spaceConfigs = computed(
  () =>
    configData.value?.documentSpaceConfigs?.nodes?.map((n) => ({
      title: n!.name as string,
      value: n!.id as string,
    })) || [],
);

const defaultSpaceConfig = ref(
  store.getters.user.mergedConfig?.defaultSpaceConfig
    ? store.getters.user.mergedConfig?.defaultSpaceConfig.toString()
    : undefined,
);

const { executeMutation: updateUserConfig } = useUpdateUserConfigMutation();
const setDefaultSpaceConfig = async (configId: string) => {
  const res = await updateUserConfig({
    configPartial: {
      defaultSpaceConfig: parseInt(configId, 10),
    },
  });
  console.log(configId, res);

  displayFlashMessage(res.error === undefined ? "success" : "error");

  if (!res.error) {
    defaultSpaceConfig.value = configId;
  }
};

const defaultMessage = ref<string>(
  store.getters.user.mergedConfig?.default_welcome_text || "",
);
const currentDefaultMessage = ref<string>(defaultMessage.value);

const unfocusText = () => {
  textElem.value.classList.remove("active");
};

const focusText = () => {
  textElem.value.classList.add("active");
};

const cancel = () => {
  currentDefaultMessage.value = defaultMessage.value;
  unfocusText();
  //displayConfirmation.value = false;
};

const updateMessage = async () => {
  const res = await updateUserConfig({
    configPartial: {
      default_welcome_text: currentDefaultMessage.value,
    },
  });

  displayFlashMessage(res.error === undefined ? "success" : "error");
};
</script>

<template>
  <label class="label">{{ $t("profile.config.label") }}</label>
  <div v-if="numSpaceConfigs > 1">
    <div class="row cols">
      <label class="label">{{
        $t("profile.config.defaultSpaceConfig.label")
      }}</label>
      <Dropdown
        :items="spaceConfigs"
        :displayTitle="true"
        :placeholder="t('profile.config.defaultSpaceConfig.placeholder')"
        :value="defaultSpaceConfig"
        @change="setDefaultSpaceConfig"
      />
    </div>
  </div>

  <div class="row">
    <label class="label">
      {{ $t("profile.config.defaultSpaceMessage.label") }}
    </label>

    <div>
      <form @submit.prevent ref="textElem" @click="focusText">
        <textarea
          v-model="currentDefaultMessage"
          @focus="focusText"
          class="textarea"
          :placeholder="$t('documentSpace.personalMessage.placeholder')"
        />
        <div class="icons">
          <span
            class="material-symbols-outlined icon primary"
            role="button"
            :tabindex="currentDefaultMessage === defaultMessage ? -1 : 0"
            :aria-disabled="currentDefaultMessage === defaultMessage"
            :aria-label="$t('submit')"
            @keydown.enter.stop.prevent="
              currentDefaultMessage !== defaultMessage && updateMessage()
            "
            @keydown.space.stop.prevent="
              currentDefaultMessage !== defaultMessage && updateMessage()
            "
            @click.stop="updateMessage"
            :class="{ disabled: currentDefaultMessage === defaultMessage }"
          >
            check
          </span>
          <span
            class="material-symbols-outlined icon"
            role="button"
            tabindex="0"
            :aria-label="$t('cancel')"
            @keydown.enter.stop.prevent="cancel"
            @keydown.space.stop.prevent="cancel"
            @click.stop="cancel"
          >
            close
          </span>
        </div>
      </form>
    </div>
  </div>
  <EmailPresets />
</template>

<style scoped lang="scss">
.row {
  margin-top: 0.5em;
  margin-bottom: 1.8em;
}

.row.cols {
  display: flex;
  align-items: center;
}

textarea {
  max-width: 100%;
  min-height: 10em;
  margin-bottom: 5px;
}

label.label {
  font-size: 0.9em;
  line-height: 1.8;
  font-weight: 500;
  margin-right: 0.5em;
  display: flex;
  align-items: center;
}

.clickable:hover {
  cursor: pointer;
}

form {
  position: relative;
}

form .icons {
  position: absolute;
  bottom: 0.6em;
  right: 0.6em;
  display: none;
}

form .icons .icon {
  font-size: 1.7rem;
  padding: 0;
}

.icon.primary {
  color: var(--classic-accent, $color-secondary);
}

.icon:hover {
  cursor: pointer;
}

form.active .icons {
  display: block;
}

.icon.disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
